import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { AlertCircle } from 'lucide-react';
import { clsx } from 'clsx';
import { useLanguage } from '../../../features/language/useLanguage';
import { AutocompleteSelect } from '../../common/AutocompleteSelect';
import { CreatableCombobox } from '../../common/CreatableCombobox';
import {
  PROCESS_TYPES,
  SPECIES_OPTIONS,
  getNestedError,
  type InventoryModalFormData,
} from './types';

interface Props {
  readOnly: boolean;
  uniqueOptions: {
    producers: string[];
    species: string[];
    varieties: string[];
  };
}

export const GreenBeanExtrinsicSection: React.FC<Props> = ({ readOnly, uniqueOptions }) => {
  const { t } = useLanguage();
  const { control, setValue, register, formState: { errors } } = useFormContext<InventoryModalFormData>();
  const enableProducer = useWatch({ control, name: 'enableProducer' });
  const enableVariety = useWatch({ control, name: 'enableVariety' });
  const speciesValue = useWatch({ control, name: 'green_bean_profile.species' });
  const processTypeSelection = useWatch({ control, name: 'processTypeSelection' });
  const processTypeOther = useWatch({ control, name: 'processTypeOther' });
  const processTypeError = getNestedError(errors, 'green_bean_profile.process_type')?.message;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-medium text-gray-900 border-b pb-2">{t('inventory.modal.sections.greenBeanExtrinsic')}</h2>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={enableProducer}
              onChange={(e) => setValue('enableProducer', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              data-test-id="inventory-create-enable-producer"
              disabled={readOnly}
            />
            <div className="flex-1 flex items-center gap-4">
              <label className="w-40 shrink-0 text-sm font-medium text-gray-700">{t('inventory.modal.fields.producer')}</label>
              <div className="flex-1">
                <Controller
                  control={control}
                  name="green_bean_profile.producer"
                  render={({ field }) => (
                    <CreatableCombobox
                      label={t('inventory.modal.fields.producer')}
                      hideLabel
                      value={field.value || ''}
                      onChange={field.onChange}
                      options={uniqueOptions.producers}
                      placeholder={t('inventory.modal.fields.producerPlaceholder')}
                      disabled={!enableProducer}
                      dataTestId="inventory-create-producer"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="h-4 w-4 mt-3 shrink-0" />
              <div className="flex-1 flex items-start gap-4">
                <label className="w-40 shrink-0 pt-2.5 text-sm font-medium text-gray-700">{t('inventory.modal.fields.species')} <span className="text-red-500">*</span></label>
                <div className="flex-1" data-field="green_bean_profile.species">
                  <Controller
                    control={control}
                    name="green_bean_profile.species"
                    render={({ field, fieldState }) => (
                      <AutocompleteSelect
                        value={field.value || ''}
                        onChange={(value) => {
                          field.onChange(value);
                          if (!value?.trim()) {
                            setValue('enableVariety', false);
                          }
                        }}
                        options={SPECIES_OPTIONS}
                        placeholder={t('inventory.modal.fields.speciesPlaceholder')}
                        error={fieldState.error?.message}
                        dataTestId="inventory-create-species"
                        allowCustom={false}
                        disabled={readOnly}
                        optionsClassName="min-w-full w-max"
                        optionClassName="whitespace-nowrap"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 pl-8">
              <input
                type="checkbox"
                checked={enableVariety}
                onChange={(e) => setValue('enableVariety', e.target.checked)}
                disabled={!speciesValue || readOnly}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                data-test-id="inventory-create-enable-variety"
              />
              <div className="flex-1 flex items-center gap-4">
                <label className="w-32 shrink-0 text-sm font-medium text-gray-700">{t('inventory.modal.fields.variety')}</label>
                <div className="flex-1">
                  <Controller
                    control={control}
                    name="green_bean_profile.variety"
                    render={({ field }) => (
                      <CreatableCombobox
                        label={t('inventory.modal.fields.variety')}
                        hideLabel
                        value={field.value || ''}
                        onChange={field.onChange}
                        options={uniqueOptions.varieties}
                        placeholder={t('inventory.modal.fields.varietyPlaceholder')}
                        disabled={!enableVariety || readOnly}
                        dataTestId="inventory-create-variety"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2" data-field="green_bean_profile.process_type">
          <div className="flex items-start gap-3">
            <div className="h-4 w-4 mt-1 shrink-0" />
            <label className="text-sm font-medium text-gray-900">
              {t('inventory.modal.fields.processType')} <span className="text-red-500">*</span>
            </label>
          </div>
          <div
            className={clsx(
              'ml-8 flex flex-col gap-2 rounded-md border p-3 transition-colors',
              processTypeError
                ? 'border-red-300 bg-red-50/40 ring-1 ring-red-200/60'
                : 'border-transparent'
            )}
          >
            {PROCESS_TYPES.map((type) => {
              const isOther = type === 'Other';
              const selected = processTypeSelection === 'Other';
              return (
                <div key={type} className="flex items-center gap-3">
                  <div className="flex items-center gap-2 w-40 shrink-0">
                    <input
                      type="radio"
                      value={type}
                      checked={processTypeSelection === type}
                      {...register('processTypeSelection', {
                        onChange: (e) => {
                          const val = e.target.value;
                          setValue('processTypeSelection', val, { shouldValidate: true });
                          if (val === 'Other') {
                            setValue('green_bean_profile.process_type', processTypeOther.trim(), { shouldValidate: true });
                          } else {
                            setValue('green_bean_profile.process_type', val, { shouldValidate: true });
                          }
                        }
                      })}
                      id={`inventory-process-type-${type}`}
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-test-id={`inventory-create-process-type-${type}`}
                      disabled={readOnly}
                    />
                    <label
                      htmlFor={`inventory-process-type-${type}`}
                      className="ml-1 text-sm text-gray-700"
                    >
                      {type}
                    </label>
                  </div>
                  <div className="flex-1 min-h-[40px]">
                    {isOther && selected && (
                      <input
                        type="text"
                        placeholder={t('inventory.modal.fields.processTypePlaceholder')}
                        value={processTypeOther}
                        onChange={(e) => {
                          const value = e.target.value;
                          setValue('processTypeOther', value);
                          setValue('green_bean_profile.process_type', value.trim(), { shouldValidate: true });
                        }}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                        data-test-id="inventory-create-process-type-other"
                        disabled={readOnly}
                      />
                    )}
                  </div>
                </div>
              );
            })}
            {processTypeError && (
              <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-red-600">
                <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                {processTypeError}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
